import { useEffect, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { useSearchParams } from "react-router-dom";
import MessageTemplatesPanel from "@/components/MessageTemplatesPanel";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Tabs from "@/components/Tabs";
import { ArrowLeft, Plus, Trash, X, ArrowRight, Star, PencilSimple, FloppyDisk, CaretUp, CaretDown } from "@phosphor-icons/react";

const KINDS = ["link", "jotform", "acuity", "upload", "custom"];

// -------------------- Reusable step editor row --------------------
function StepRow({ step, i, total, onSave, onDelete, onMove }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(step);
  useEffect(() => setF(step), [step]);
  const save = async () => {
    try { await onSave(f); setEdit(false); toast.success("Step saved"); }
    catch (_e) { toast.error("Save failed"); }
  };
  if (!edit) return (
    <div className="hair p-3" data-testid={`tpl-step-${step.step_id || i}`}>
      <div className="flex items-start md:items-center gap-3">
        <span className="title-xl text-lg opacity-40 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{step.title}</p>
          <p className="label mt-0.5 truncate">{step.kind}{step.url ? ` · ${step.url}` : ""}</p>
        </div>
        <div className="hidden md:flex items-center gap-1 shrink-0">
          <button data-testid={`tpl-step-up-${step.step_id || i}`} disabled={i === 0}
            onClick={() => onMove(step, -1)} className="btn-ghost text-xs p-1.5 disabled:opacity-30">
            <CaretUp size={12} weight="bold" />
          </button>
          <button data-testid={`tpl-step-down-${step.step_id || i}`} disabled={i === total - 1}
            onClick={() => onMove(step, 1)} className="btn-ghost text-xs p-1.5 disabled:opacity-30">
            <CaretDown size={12} weight="bold" />
          </button>
          <button data-testid={`tpl-step-edit-${step.step_id || i}`} onClick={() => setEdit(true)} className="btn-ghost text-xs p-1.5">
            <PencilSimple size={12} weight="bold" />
          </button>
          <button data-testid={`tpl-step-del-${step.step_id || i}`} onClick={() => onDelete(step)} className="btn-ghost text-xs p-1.5">
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>
      <div className="flex md:hidden items-center gap-1 mt-2 pl-9">
        <button data-testid={`tpl-step-up-m-${step.step_id || i}`} disabled={i === 0}
          onClick={() => onMove(step, -1)} className="btn-ghost text-xs p-1.5 disabled:opacity-30">
          <CaretUp size={12} weight="bold" />
        </button>
        <button data-testid={`tpl-step-down-m-${step.step_id || i}`} disabled={i === total - 1}
          onClick={() => onMove(step, 1)} className="btn-ghost text-xs p-1.5 disabled:opacity-30">
          <CaretDown size={12} weight="bold" />
        </button>
        <button data-testid={`tpl-step-edit-m-${step.step_id || i}`} onClick={() => setEdit(true)} className="btn-ghost text-xs p-1.5">
          <PencilSimple size={12} weight="bold" />
        </button>
        <button data-testid={`tpl-step-del-m-${step.step_id || i}`} onClick={() => onDelete(step)} className="btn-ghost text-xs p-1.5">
          <Trash size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
  return (
    <div className="hair p-3 space-y-2" data-testid={`tpl-step-edit-form-${step.step_id || i}`}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        <input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })}
          data-testid={`tpl-step-title-${step.step_id || i}`}
          placeholder="Title" className="px-3 py-2 md:col-span-2" />
        <input value={f.url || ""} onChange={(e) => setF({ ...f, url: e.target.value })}
          data-testid={`tpl-step-url-${step.step_id || i}`}
          placeholder="URL" className="px-3 py-2" />
        <select value={f.kind || "link"} onChange={(e) => setF({ ...f, kind: e.target.value })}
          data-testid={`tpl-step-kind-${step.step_id || i}`}
          className="px-3 py-2">
          {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <input type="number" value={f.order ?? 0} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || "0", 10) })}
          data-testid={`tpl-step-order-${step.step_id || i}`}
          placeholder="Order" className="px-3 py-2" />
      </div>
      <textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })}
        data-testid={`tpl-step-desc-${step.step_id || i}`}
        placeholder="Description" rows={2} className="w-full px-3 py-2" />
      <div className="flex gap-2">
        <button data-testid={`tpl-step-save-${step.step_id || i}`} onClick={save} className="btn-primary text-xs">
          <FloppyDisk size={12} weight="bold" /> Save step
        </button>
        <button onClick={() => { setF(step); setEdit(false); }} className="btn-ghost text-xs">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}

function AddStepInline({ onAdd }) {
  const [t, setT] = useState({ title: "", description: "", kind: "link", url: "", order: 99 });
  const submit = (e) => {
    e.preventDefault();
    if (!t.title) return;
    onAdd(t);
    setT({ title: "", description: "", kind: "link", url: "", order: 99 });
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-3" data-testid="tpl-add-step-form">
      <input required placeholder="Step title" value={t.title}
        data-testid="tpl-add-title" onChange={(e) => setT({ ...t, title: e.target.value })} className="px-3 py-2 md:col-span-2" />
      <input placeholder="URL" value={t.url}
        data-testid="tpl-add-url" onChange={(e) => setT({ ...t, url: e.target.value })} className="px-3 py-2" />
      <select value={t.kind} data-testid="tpl-add-kind"
        onChange={(e) => setT({ ...t, kind: e.target.value })} className="px-3 py-2">
        {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <button data-testid="tpl-add-btn" className="btn-primary justify-center">
        <Plus size={14} weight="bold" /> Add
      </button>
    </form>
  );
}

// -------------------- Workflow templates panel (onboarding OR offboarding) --------------------
function WorkflowTemplatesPanel({ kind }) {
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/templates?kind=${kind}`);
    setTemplates(data);
  };
  useEffect(() => { load(); }, [kind]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/templates", { name: newName, tasks: [], is_default: false, kind });
      toast.success("Template created");
      setNewName(""); setShowNew(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const setDefault = async (tpl) => {
    await api.patch(`/admin/templates/${tpl.template_id}`, {
      name: tpl.name, tasks: tpl.tasks, is_default: true, kind: tpl.kind || kind,
    });
    toast.success(`"${tpl.name}" is now the default ${kind} workflow`); load();
  };
  const del = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    await api.delete(`/admin/templates/${tpl.template_id}`); load();
  };
  const renameTpl = async (tpl, name) => {
    await api.patch(`/admin/templates/${tpl.template_id}`, { name, tasks: tpl.tasks, is_default: tpl.is_default, kind: tpl.kind || kind });
    load();
  };
  const addStep = async (tpl, task) => {
    await api.post(`/admin/templates/${tpl.template_id}/tasks`, task);
    toast.success("Step added"); load();
  };
  const saveStep = async (tpl, step) => {
    await api.patch(`/admin/templates/${tpl.template_id}/tasks/${step.step_id}`, {
      title: step.title, description: step.description, kind: step.kind, url: step.url, order: step.order,
    });
    load();
  };
  const delStep = async (tpl, step) => {
    if (!window.confirm(`Delete step "${step.title}"?`)) return;
    await api.delete(`/admin/templates/${tpl.template_id}/tasks/${step.step_id}`);
    load();
  };
  const moveStep = async (tpl, step, delta) => {
    const ids = (tpl.tasks || []).map(s => s.step_id);
    const idx = ids.indexOf(step.step_id);
    if (idx < 0) return;
    const next = Math.max(0, Math.min(ids.length - 1, idx + delta));
    if (next === idx) return;
    [ids[idx], ids[next]] = [ids[next], ids[idx]];
    await api.patch(`/admin/templates/${tpl.template_id}/tasks/reorder`, { ids });
    load();
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-templates-panel`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-3">
        <p className="flex-1" style={{ color: "var(--text-dim)" }}>
          {kind === "onboarding"
            ? "These workflows auto-assign to new clients. The default (★) applies when no template is chosen."
            : "Offboarding workflows are applied when a client's project is wrapping up. The default (★) is applied when offboarding is enabled without an explicit template."}
        </p>
        <button data-testid={`new-${kind}-tpl-btn`} onClick={() => setShowNew(true)} className="btn-primary shrink-0 whitespace-nowrap">
          <Plus size={14} weight="bold" /> New template
        </button>
      </div>

      {templates.length === 0 && (
        <div className="panel p-10 text-center label">No {kind} templates yet</div>
      )}

      {templates.map((tpl) => (
        <div key={tpl.template_id} className="panel p-4 md:p-6" data-testid={`tpl-${tpl.template_id}`}>
          <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {tpl.is_default && <Star size={16} weight="fill" className="shrink-0" />}
              <input
                value={tpl.name}
                data-testid={`tpl-name-${tpl.template_id}`}
                onBlur={(e) => { if (e.target.value !== tpl.name) renameTpl(tpl, e.target.value); }}
                onChange={(e) => setTemplates(list => list.map(x => x.template_id === tpl.template_id ? { ...x, name: e.target.value } : x))}
                className="title-xl text-xl md:text-2xl px-2 py-1 min-w-0 flex-1"
                style={{ background: "transparent", border: "none" }}
              />
              <span className="label shrink-0">{tpl.tasks?.length || 0} steps</span>
            </div>
            <div className="flex gap-2 shrink-0">
              {!tpl.is_default && (
                <button data-testid={`tpl-set-default-${tpl.template_id}`} onClick={() => setDefault(tpl)} className="btn-ghost text-xs">
                  <Star size={12} /> Set as default
                </button>
              )}
              <button data-testid={`tpl-del-${tpl.template_id}`} onClick={() => del(tpl)} className="btn-ghost text-xs">
                <Trash size={12} weight="bold" />
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {(tpl.tasks || []).map((step, i) => (
              <StepRow
                key={step.step_id || i}
                step={step} i={i} total={(tpl.tasks || []).length}
                onSave={(next) => saveStep(tpl, next)}
                onDelete={(s) => delStep(tpl, s)}
                onMove={(s, d) => moveStep(tpl, s, d)}
              />
            ))}
            <AddStepInline onAdd={(task) => addStep(tpl, task)} />
          </div>
        </div>
      ))}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="panel w-full max-w-md p-8">
            <div className="flex justify-between items-start mb-4">
              <h3 className="title-xl text-2xl">New {kind} template</h3>
              <button onClick={() => setShowNew(false)} className="btn-ghost p-2"><X size={16} /></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <input required autoFocus placeholder="Template name" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid={`new-${kind}-tpl-name`}
                className="w-full px-4 py-3" />
              <button data-testid={`new-${kind}-tpl-submit`} className="btn-primary w-full justify-center h-12">
                Create <ArrowRight size={14} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Sample templates panel --------------------
function SampleTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/sample-templates").then(r => setTemplates(r.data));
  useEffect(() => { load(); }, []);
  const del = async (t) => {
    if (!window.confirm(`Delete sample template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/sample-templates/${t.template_id}`);
      toast.success("Deleted"); load();
    } catch (_e) { toast.error("Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="sample-templates-panel">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-3">
        <p className="flex-1" style={{ color: "var(--text-dim)" }}>
          Save canned sample content (title, description, link) you can drop into any client's sample send.
        </p>
        <button data-testid="new-sample-tpl-btn" onClick={() => { setEditing(null); setShowAdd(true); }} className="btn-primary shrink-0 whitespace-nowrap">
          <Plus size={14} weight="bold" /> New template
        </button>
      </div>
      {templates.length === 0 ? (
        <div className="panel p-10 text-center label">No sample templates yet</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.template_id} className="panel p-5" data-testid={`sample-tpl-${t.template_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="title-xl text-lg truncate">{t.name}</p>
                  <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>Title: {t.title}</p>
                  {t.description && <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>{t.description}</p>}
                  {t.url && <p className="text-xs mt-1 truncate" style={{ color: "var(--text-dim)" }}>{t.url}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  <button data-testid={`edit-sample-tpl-${t.template_id}`}
                    onClick={() => { setEditing(t); setShowAdd(true); }} className="btn-ghost text-xs">
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  <button data-testid={`del-sample-tpl-${t.template_id}`}
                    onClick={() => del(t)} className="btn-ghost text-xs">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && (
        <SampleTemplateModal
          initial={editing}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}
function SampleTemplateModal({ initial, onClose, onDone }) {
  const [form, setForm] = useState(initial || { name: "", title: "", description: "", url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (initial) await api.patch(`/admin/sample-templates/${initial.template_id}`, form);
      else await api.post(`/admin/sample-templates`, form);
      toast.success(initial ? "Updated" : "Created");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="panel w-full max-w-md p-8">
        <div className="flex justify-between items-start mb-4">
          <h3 className="title-xl text-2xl">{initial ? "Edit" : "New"} sample template</h3>
          <button onClick={onClose} className="btn-ghost p-2"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Template name (internal)" value={form.name}
            data-testid="sample-tpl-name" onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3" />
          <input required placeholder="Sample title (shown to client)" value={form.title}
            data-testid="sample-tpl-title" onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-3" />
          <textarea placeholder="Description" value={form.description}
            data-testid="sample-tpl-desc" onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3} className="w-full px-4 py-3" />
          <input placeholder="Optional link URL" value={form.url || ""}
            data-testid="sample-tpl-url" onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="w-full px-4 py-3" />
          <button data-testid="sample-tpl-submit" disabled={busy} className="btn-primary w-full justify-center h-12">
            <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

// -------------------- Main page --------------------
export default function AdminTemplates() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "onboarding";
  const setTab = (t) => setSp({ tab: t });
  const tabs = [
    { key: "onboarding", label: "Tasks" },
    { key: "offboarding", label: "Offboarding" },
    { key: "sample", label: "Samples" },
    { key: "messages", label: "Messages" },
  ];
  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-templates">
      <header className="flex items-center justify-between px-4 md:px-6 py-6 border-b gap-3" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <NavButtons home="/admin" testid="tpl-back" />
          <div className="min-w-0">
            <p className="label">Workflow</p>
            <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Templates</h1>
          </div>
        </div>
      </header>
      <div className="px-4 md:px-6 pt-6">
        <Tabs tabs={tabs} active={tab} onChange={setTab} testid="templates-tabs" />
      </div>
      <div className="p-4 md:p-6">
        {tab === "onboarding" && <WorkflowTemplatesPanel kind="onboarding" />}
        {tab === "offboarding" && <WorkflowTemplatesPanel kind="offboarding" />}
        {tab === "sample" && <SampleTemplatesPanel />}
        {tab === "messages" && <MessageTemplatesPanel />}
      </div>
    </div>
  );
}
